import type { ReactNode } from "react";
import { Flex, Text, ValueParser } from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import {
  type DebtMarketValue,
  debtMarketValueOptions,
  type MortgageInterestRate,
} from "../../../types/MortgageInterestRate";
import {
  filterGridSecondColumnStyle,
  filterGridThirdColumnStyle,
  filterGridStyle,
  subGridStyle,
  interestRateRowImageStyle,
  listRowStyle,
  filterGridFirstColumnStyle,
  valueParserStyle,
  ctaSmallStyle,
  ctaStyle,
} from "../InterestRatesOverview.css";

export type InterestRateRowProps = {
  /** The mortgage interest rate data to display */
  interestRate: MortgageInterestRate;
  /** The currently selected debt market value */
  debtMarketValue: DebtMarketValue;
  /** Call-to-action element */
  cta?: ReactNode;
  /** Small call-to-action element for mobile */
  ctaSmall?: ReactNode;
  /** The image to display */
  image?: ReactNode;
};

/**
 * InterestRateRow component displays a single row of mortgage interest rate data.
 */
export const InterestRateRow = ({
  interestRate,
  image,
  debtMarketValue,
  cta,
  ctaSmall,
}: InterestRateRowProps) => (
  <div className={mergeClasses(filterGridStyle, listRowStyle)}>
    <Flex className={filterGridFirstColumnStyle} gap="m" align="center">
      {image ? (
        image
      ) : (
        <img
          src={interestRate.image}
          alt={interestRate.name}
          className={interestRateRowImageStyle}
        />
      )}
      <Text grow color="currentColor50">
        {interestRate.productName ?? interestRate.name}
      </Text>
    </Flex>
    <div className={mergeClasses(filterGridSecondColumnStyle, subGridStyle)}>
      {debtMarketValueOptions.map((option, i) => (
        <div
          key={i}
          className={valueParserStyle({
            active: debtMarketValue === option.value,
          })}
        >
          {!interestRate.debtMarketValues?.[option.value] ||
          interestRate.debtMarketValues[option.value] === 0 ? (
            "—"
          ) : (
            <ValueParser
              value={{
                value: interestRate.debtMarketValues[option.value] ?? 0,
                type: "percentage",
              }}
            />
          )}
        </div>
      ))}
    </div>
    <Flex
      className={filterGridThirdColumnStyle}
      justify="flex-end"
      align="center"
    >
      <div className={ctaSmallStyle}>{ctaSmall}</div>
      <div className={ctaStyle}>{cta}</div>
    </Flex>
  </div>
);

InterestRateRow.displayName = "InterestRateRow";
